import { Box, Card, Stack, Text } from '@mantine/core';

import { T } from '@/theme/assay';
import { TIER_MAP, type Tier } from '@/types/grade';

interface TierStrataCardProps {
  tier: Tier;
  /** Order in the row - drives the stagger delay. */
  index: number;
  /** Toggles the entrance animation; pass `true` once the section enters the viewport. */
  inView: boolean;
}

// Hero-scale strata bar that fills with overshoot easing, parameterised by
// --bar-fill so each tier can supply its own target height. Used only here -
// the in-app StrataBar atom is sized for tighter contexts (e.g. pipeline rows).
function HeroStrataBar({
  tier,
  inView,
  fillDelay,
  glintDelay,
}: {
  tier: Tier;
  inView: boolean;
  fillDelay: number;
  glintDelay: number;
}) {
  const meta = TIER_MAP[tier];
  const showGlint = meta.fillPct >= 50;

  return (
    <Box
      style={{
        position: 'relative',
        width: 18,
        height: 240,
        backgroundColor: '#0c0906',
        border: `1px solid ${T.border}`,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: 'inset 0 0 12px rgba(0,0,0,0.6)',
        flexShrink: 0,
      }}
    >
      {/* horizontal strata reference lines */}
      {[0.2, 0.45, 0.7, 0.88].map((p) => (
        <Box
          key={p}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${p * 100}%`,
            height: 1,
            background: 'rgba(255,255,255,0.045)',
            zIndex: 3,
          }}
        />
      ))}

      {/* the ore fill - height animates from 0 to --bar-fill with overshoot */}
      <Box
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: inView ? `${meta.fillPct}%` : 0,
          background: meta.gradient,
          zIndex: 2,
          ['--bar-fill' as string]: `${meta.fillPct}%`,
          animation: inView
            ? `assay-tier-bar-fill 1.5s cubic-bezier(0.34, 1.1, 0.64, 1) ${fillDelay}s both`
            : undefined,
        }}
      />

      {/* gold seam glint above the fill - emerges once the fill settles */}
      {showGlint && (
        <Box
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${meta.fillPct}%`,
            height: 2,
            background: meta.hexColor,
            boxShadow: `0 0 8px ${meta.hexColor}, 0 0 4px ${meta.hexColor}`,
            opacity: 0,
            transformOrigin: 'center',
            animation: inView ? `assay-tier-glint-in 0.5s ease ${glintDelay}s forwards` : undefined,
            zIndex: 4,
          }}
        />
      )}
    </Box>
  );
}

export function TierStrataCard({ tier, index, inView }: TierStrataCardProps) {
  const meta = TIER_MAP[tier];

  // Stagger from the section's `inView` moment - mirrors the design timing.
  const cardDelay = 0.6 + index * 0.12;
  const fillDelay = cardDelay + 0.9;
  const glintDelay = fillDelay + 1.05;
  const labelDelay = fillDelay + 0.6;

  return (
    <Card
      withBorder
      padding="md"
      radius="sm"
      style={{
        width: 240,
        backgroundColor: 'var(--assay-surface)',
        backgroundImage:
          'radial-gradient(ellipse at 100% 0%, rgba(196, 152, 58, 0.045) 0%, transparent 55%)',
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(24px)',
        animation: inView
          ? `assay-word-rise 0.55s cubic-bezier(0.22, 1, 0.36, 1) ${cardDelay}s both`
          : undefined,
      }}
    >
      <Stack gap="sm">
        <Box style={{ display: 'flex', alignItems: 'flex-end', gap: 14, height: 240 }}>
          <HeroStrataBar
            tier={tier}
            inView={inView}
            fillDelay={fillDelay}
            glintDelay={glintDelay}
          />
          <Box
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
              paddingBottom: 2,
              opacity: 0,
              animation: inView ? `assay-word-rise 0.5s ease ${labelDelay}s both` : undefined,
            }}
          >
            <Text
              style={{
                fontFamily: '"DM Mono", monospace',
                fontSize: '0.6rem',
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: meta.hexColor,
                marginBottom: 4,
              }}
            >
              {meta.shortLabel}
            </Text>
            <Text
              style={{
                fontFamily: '"Cormorant Garamond", serif',
                fontWeight: 400,
                fontSize: '1.125rem',
                color: 'var(--assay-text-primary)',
                lineHeight: 1.15,
              }}
            >
              {meta.label}
            </Text>
          </Box>
        </Box>

        <Text
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.7rem',
            lineHeight: 1.55,
            color: 'var(--assay-text-label)',
            opacity: 0,
            animation: inView ? `assay-word-rise 0.5s ease ${labelDelay + 0.08}s both` : undefined,
          }}
        >
          {meta.description}
        </Text>

        <Text
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: '0.6rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--assay-text-muted)',
            opacity: 0,
            animation: inView ? `assay-word-rise 0.5s ease ${labelDelay + 0.16}s both` : undefined,
          }}
        >
          within {meta.deferralDays} days
        </Text>
      </Stack>
    </Card>
  );
}
